import { DataProxy } from "apollo-cache";
import gql from "graphql-tag";
import { useCallback } from "react";
import { useMutation } from "react-apollo";

import {
  DisableLearnerAllowanceMutation,
  DisableLearnerAllowanceMutationVariables,
  EnableDisableLearnerAllowanceFragmentFragment,
  EnableLearnerAllowanceMutation,
  EnableLearnerAllowanceMutationVariables
} from "../../../../shared/generated/graphql";

export const EnableDisableLearnerAllowanceFragment = gql`
  fragment EnableDisableLearnerAllowanceFragment on LearnerAllowance {
    uid
    status
    confirmedAt
    stoppedAt
    stoppedReason
    paymentSchedule {
      uid
      amountCents
      startTime
      nextBillingDate
    }
  }
`;

const enableLearnerAllowanceMutationStr = gql`
  mutation EnableLearnerAllowance(
    $enableLearnerAllowanceInput: EnableLearnerAllowanceInput!
  ) {
    enableLearnerAllowance(
      enableLearnerAllowanceInput: $enableLearnerAllowanceInput
    ) {
      ...EnableDisableLearnerAllowanceFragment
    }
  }
  ${EnableDisableLearnerAllowanceFragment}
`;

const disableLearnerAllowanceMutationStr = gql`
  mutation DisableLearnerAllowance(
    $disableLearnerAllowanceInput: DisableLearnerAllowanceInput!
  ) {
    disableLearnerAllowance(
      disableLearnerAllowanceInput: $disableLearnerAllowanceInput
    ) {
      ...EnableDisableLearnerAllowanceFragment
    }
  }
  ${EnableDisableLearnerAllowanceFragment}
`;

function updateCache(
  cache: DataProxy,
  data: EnableDisableLearnerAllowanceFragmentFragment | undefined
) {
  const id = data?.uid;
  if (id) {
    cache.writeFragment({
      id,
      data,
      fragment: EnableDisableLearnerAllowanceFragment
    });
  }
}

export type UseEnableDisableLearnerAllowanceResp = {
  enableLearnerAllowance: () => Promise<
    EnableLearnerAllowanceMutation["enableLearnerAllowance"] | undefined
  >;
  disableLearnerAllowance: (
    stoppedReason?: string
  ) => Promise<
    DisableLearnerAllowanceMutation["disableLearnerAllowance"] | undefined
  >;
  isEnabling: boolean;
  isDisabling: boolean;
  enableAllowanceData?: EnableLearnerAllowanceMutation["enableLearnerAllowance"];
  disableAllowanceData?: DisableLearnerAllowanceMutation["disableLearnerAllowance"];
};

export function useEnableDisableLearnerAllowance(
  learnerAllowanceUid: string
): UseEnableDisableLearnerAllowanceResp {
  const [
    enableLearnerAllowance,
    { loading: isEnabling, data: enableAllowanceData }
  ] = useMutation<
    EnableLearnerAllowanceMutation,
    EnableLearnerAllowanceMutationVariables
  >(enableLearnerAllowanceMutationStr, {
    update(cache, { data }) {
      const enableLearnerAllowance = data?.enableLearnerAllowance;
      updateCache(cache, enableLearnerAllowance);
    }
  });
  const [
    disableLearnerAllowance,
    { loading: isDisabling, data: disableAllowanceData }
  ] = useMutation<
    DisableLearnerAllowanceMutation,
    DisableLearnerAllowanceMutationVariables
  >(disableLearnerAllowanceMutationStr, {
    update(cache, { data }) {
      const disableLearnerAllowance = data?.disableLearnerAllowance;
      updateCache(cache, disableLearnerAllowance);
    }
  });
  return {
    enableLearnerAllowance: useCallback(async () => {
      const { data } = await enableLearnerAllowance({
        variables: { enableLearnerAllowanceInput: { learnerAllowanceUid } }
      });
      return data?.enableLearnerAllowance;
    }, [enableLearnerAllowance, learnerAllowanceUid]),
    disableLearnerAllowance: useCallback(
      async (stoppedReason?: string) => {
        const { data } = await disableLearnerAllowance({
          variables: {
            disableLearnerAllowanceInput: { learnerAllowanceUid, stoppedReason }
          }
        });
        return data?.disableLearnerAllowance;
      },
      [disableLearnerAllowance, learnerAllowanceUid]
    ),
    isEnabling,
    isDisabling,
    enableAllowanceData: enableAllowanceData?.enableLearnerAllowance,
    disableAllowanceData: disableAllowanceData?.disableLearnerAllowance
  };
}
